import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ConversationType, Message, MessageType, Partner, TripMatch, TripMatchStatus,
} from '@marageti/z4-sdk/lib/travel';
import { RoleName } from '@marageti/z4-sdk/lib/auth';
import {
  TemplatePluginHandle,
  useApiClient,
  useFetch,
  ComposerRestoreContent,
} from '@marageti/z4-lib';
import { useSnackbar } from 'notistack';
import { PersonType } from '@marageti/z4-sdk/lib/people';
import { TravelerProfile } from '@marageti/z4-sdk/lib/people/TravelerProfile';
import ZicassoChatView from './zicasso-chat-view';
import useZicassoChat from '../../hooks/use-zicasso-chat';
import { useAuthStore } from '../../store';
import { getConversationTypeFromTripMatch } from '../../utils/helpers';

const ZicassoChatContainer = () => {
  //  There will always be a conversationId in the params, but not always a tripMatchId in the params
  const { tripMatchId: tripMatchIdFromParams, conversationId } = useParams();
  const navigate = useNavigate();
  const apiClient = useApiClient();
  const { enqueueSnackbar } = useSnackbar();
  const { loggedInAgent } = useAuthStore();
  const {
    conversation,
    isError,
    isLoading,
    isValidating,
    messages,
    participants,
    sendMessage,
    lastSeenMessageId,
    loadMoreMessages,
    hasMoreMessages,
    updateMessages,
    deleteMessage,
  } = useZicassoChat({
    conversationId,
    viewerId: loggedInAgent?.id,
    tripMatchId: tripMatchIdFromParams,
  });
  const viewer = {
    id: loggedInAgent?.id,
    type: PersonType.Agent,
  };

  // Use tripMatchId from params when present (e.g. trip-match messages), otherwise from the fetched conversation
  const tripMatchId = tripMatchIdFromParams ?? conversation?.tripMatchId;

  const [openDialog, setOpenDialog] = useState('');
  const [restoreEditorContent, setRestoreEditorContent] = useState<
  ComposerRestoreContent | undefined
  >(undefined);

  useEffect(() => {
    setRestoreEditorContent(undefined);
  }, [conversationId]);

  const { data: tripMatch, mutate: mutateTripMatch } = useFetch<TripMatch>(
    apiClient.tripMatchesClient.getTripMatch,
    [tripMatchId],
    !!tripMatchId,
  );

  const afterTripMatchDocumentUpload = useCallback(async () => {
    await updateMessages();
    if (tripMatchId) {
      await mutateTripMatch();
    }
  }, [updateMessages, tripMatchId, mutateTripMatch]);
  const conversationType = getConversationTypeFromTripMatch(tripMatch, conversationId);
  const blockMessaging = conversationType === ConversationType.TravelerToPartner && tripMatch?.status === TripMatchStatus.MatchPending;
  const isInternal = conversationType !== ConversationType.ZicassoToPartner;
  const readOnly = tripMatch?.status === TripMatchStatus.MatchAccepted && !!loggedInAgent?.userInfo[0]?.roles?.includes(RoleName.AGENT_SUPPORT);

  const { data: partner } = useFetch<Partner>(
    apiClient.partnersClient.getPartner,
    [loggedInAgent?.partnerId],
    !!loggedInAgent?.partnerId && conversationType === ConversationType.TravelerToPartner,
  );
  const hasPartnerSmsNumber = !!partner?.smsPhoneNumber;
  const travelerId = tripMatch?.travelers[0]?.id;
  const hasTravelerPhoneNumber = !!tripMatch?.travelers[0]?.phone?.number;
  const travelerPhonePrefix = tripMatch?.travelers[0]?.phone?.prefix?.trim();
  const isInternationalPhoneNumber = travelerPhonePrefix !== '+1' && travelerPhonePrefix !== '1';

  const { data: travelerProfile } = useFetch<TravelerProfile>(
    apiClient.peopleClient.getTravelerProfile,
    [travelerId],
    !!travelerId
      && conversationType === ConversationType.TravelerToPartner
      && hasPartnerSmsNumber
      && hasTravelerPhoneNumber,
  );
  const isSmsOptedOut = travelerProfile?.preferences?.smsOptOut ?? false;

  const templatePluginRef = useRef<TemplatePluginHandle>(null);

  const setTemplateText = (value: string) => {
    if (templatePluginRef.current) {
      templatePluginRef.current.insertTemplate(value);
    }
  };

  const closeDialog = () => setOpenDialog('');

  const handleDocumentClick = async (message: Message) => {
    if (!message.entityId) {
      return;
    }

    try {
      const doc = await apiClient.documentsClient.getDocument(message.entityId);
      if (doc.sourceURI) {
        window.open(doc.sourceURI, '_blank');
        return;
      }

      const downloadURL = await apiClient.documentsClient.getDocumentDownloadUrl(message.entityId);
      window.open(downloadURL.url, '_blank');
    } catch (error: any) {
      const snackbarMessage = error?.code === 404 ? 'Document not found' : 'Error downloading document';
      enqueueSnackbar(snackbarMessage, { variant: 'default' });
    }
  };

  const handleSalesRecordingClick = async (message: Message) => {
    const salesRecordingId = message.entityId;
    if (salesRecordingId) {
      navigate(`/trip-matches/${tripMatchId}/sales/${salesRecordingId}`);
    }
  };

  const handleNonEntityViewClick = (message: Message) => {
    if (message.messageType === MessageType.TripMatchCanceled) {
      navigate(`/trip-matches/${tripMatchId}`);
    }
  };

  const handleViewClick = (message: Message) => {
    switch (message.entityType) {
      case 'Document':
        handleDocumentClick(message);
        break;
      case 'SalesRecording':
        handleSalesRecordingClick(message);
        break;
      default:
        handleNonEntityViewClick(message);
        break;
    }
  };

  const handleOnSend = (text: string) => {
    sendMessage({ conversationId, text });
  };

  const handleUndoSend = async (message: Message) => {
    const html = message.text ?? '';
    try {
      await deleteMessage(message.id);
      setRestoreEditorContent({ key: Date.now(), html });
    } catch {
      enqueueSnackbar('Failed to delete message.', { variant: 'default' });
    }
  };

  return (
    <ZicassoChatView
      blockMessaging={blockMessaging}
      closeDialog={closeDialog}
      conversation={conversation}
      conversationType={conversationType}
      handleViewClick={handleViewClick}
      isError={isError}
      isInternal={isInternal}
      isLoading={isLoading}
      isValidating={isValidating}
      messages={messages}
      lastMessageSeenId={lastSeenMessageId}
      loadMoreMessages={loadMoreMessages}
      hasMoreMessages={hasMoreMessages}
      openDialog={openDialog}
      participants={participants}
      onSend={handleOnSend}
      readOnly={readOnly}
      setOpenDialog={setOpenDialog}
      setTemplateText={setTemplateText}
      templatePluginRef={templatePluginRef}
      tripMatchId={tripMatchId || ''}
      viewer={viewer}
      updateMessages={updateMessages}
      travelerId={travelerId}
      hasPartnerSmsNumber={hasPartnerSmsNumber}
      hasTravelerPhoneNumber={hasTravelerPhoneNumber}
      isSmsOptedOut={isSmsOptedOut}
      isInternationalPhoneNumber={isInternationalPhoneNumber}
      afterTripMatchDocumentUpload={afterTripMatchDocumentUpload}
      onUndoSend={handleUndoSend}
      restoreEditorContent={restoreEditorContent}
    />
  );
};

export default ZicassoChatContainer;
